import { type HTMLAttributes, useEffect, useRef } from 'react';

import Icon from 'components/Icon';
import Img from 'components/Img';
import Link from 'components/Link';
import SwipeWrapper from 'components/SwipeWrapper';
import { useMaxWidth } from 'hooks';
import { usePath } from 'hooks/navigation';
import type { HTMLBlockContainerTagName } from 'types';
import { cn } from 'utils/classNames';

interface Item {
	href: string;
	imageSrc: string | undefined;
	text: string;
}

interface Props extends HTMLAttributes<HTMLElement> {
	containerTag?: HTMLBlockContainerTagName;
	isCarouselOnSmallScreens?: boolean;
	items: Item[];
}

export default function CategoryNavigation({
	containerTag: Container = 'div',
	isCarouselOnSmallScreens = true,
	items,
	...attrs
}: Props) {
	const path = usePath();
	const subcategoryListRef = useRef<HTMLUListElement>(null);
	const isMaxMd = useMaxWidth('md');

	useEffect(() => {
		if (isCarouselOnSmallScreens && isMaxMd) {
			subcategoryListRef.current
				?.querySelector('[data-active]')
				?.scrollIntoView({ block: 'nearest', inline: 'center' });
		}
	}, [isCarouselOnSmallScreens, isMaxMd]);

	return (
		<Container {...attrs}>
			<SwipeWrapper
				ref={subcategoryListRef}
				activeClassName={cn(
					isCarouselOnSmallScreens && 'md:hidden',
					!isCarouselOnSmallScreens && 'hidden',
				)}
				inactiveClassName={cn(
					'grid grid-cols-2 gap-r-md sm:grid-cols-3 min-[800px]:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-8',
					isCarouselOnSmallScreens && 'max-md:hidden',
				)}
				spacing={1}
				pullGutters
			>
				{items.map((item) => (
					<li
						key={item.href}
						// Used with ref for scrolling, see effect.
						data-active={path.is(item.href) ? '' : undefined}
						className={
							isCarouselOnSmallScreens
								? 'max-md:w-36 max-md:shrink-0'
								: undefined
						}
					>
						<Link
							href={item.href}
							className="group/category-link block wrap-break-word whitespace-normal hover:underline max-md:text-sm"
						>
							<div
								className={cn(
									'mb-2 flex h-18 items-center justify-center rounded-xs bg-subtle group-hover/category-link:bg-subtleHover md:mb-3 md:h-24',
									path.is(item.href) && 'outline-2 outline-focus outline-solid',
								)}
							>
								{item.imageSrc && (
									<Img
										src={item.imageSrc}
										alt=""
										width={80}
										height={80}
										className="inline-block size-16 md:size-20"
										jpgOptimized={false}
									/>
								)}
								{!item.imageSrc && <Icon name="j" size={40} />}
							</div>
							{item.text}
						</Link>
					</li>
				))}
			</SwipeWrapper>
		</Container>
	);
}
CategoryNavigation.displayName = 'CategoryNavigation';
